import { h, svgUse } from '../../../utils/dom.js';
import { t } from '../../../i18n/index.js';
import { botService } from '../../../services/botService.js';

const POSITION_KEY = 'ludus_bot_position';
const VISIBILITY_KEY = 'ludus_bot_hidden';
const EDGE_GAP = 12;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

export function StudyBot() {
  const body = h('div', { class: 'bot-body', id: 'botBody' },
    h('div', { class: 'msg bot' }, t('botGreet')),
  );

  const input = h('input', {
    type: 'text',
    placeholder: t('botPh'),
    'aria-label': t('botPh'),
  });
  const send = h('button', { class: 'send-btn', type: 'button', 'aria-label': t('botSend') }, svgUse('ic-send'));
  const close = h('button', { class: 'bot-close', type: 'button', 'aria-label': t('botClose') }, svgUse('ic-close'));
  const hide = h('button', { class: 'bot-hide', type: 'button', 'aria-label': t('botHide'), title: t('botHide') }, svgUse('ic-eye-off'));
  const showTab = h('button', { class: 'bot-show-tab', type: 'button', 'aria-label': t('botShow'), title: t('botShow') },
    svgUse('ic-eye'),
    h('span', {}, t('botShow')),
  );
  const launcher = h('button', {
    class: 'bot-launcher',
    type: 'button',
    'aria-label': t('botOpen'),
    'aria-expanded': 'false',
  },
    h('span', { class: 'bot-speech' }, t('botGreet')),
    h('span', { class: 'bot-mascot-wrap' },
      h('img', { class: 'bot-mascot', src: '/ludus-study-companion.png', alt: '' }),
      h('span', { class: 'bot-online', 'aria-hidden': 'true' }),
    ),
  );

  const dragHandle = h('div', { class: 'bot-drag-handle' },
    h('img', { class: 'bot-head-mascot', src: '/ludus-study-companion.png', alt: '' }),
    h('div', { class: 'bot-title' }, h('b', {}, t('botName')), h('span', {}, t('botSub'))),
    h('span', { class: 'bot-drag-hint', title: t('botDrag') }, svgUse('ic-grip')),
    close,
  );

  const panel = h('section', { class: 'card bot-panel', 'aria-label': t('botName') },
    dragHandle,
    body,
    h('div', { class: 'bot-foot' }, input, send),
  );

  const launcherWrap = h('div', { class: 'bot-launcher-wrap' }, launcher, hide);
  const root = h('div', { class: 'bot-widget' }, panel, launcherWrap, showTab);
  let isOpen = false;
  let dragged = false;

  function setOpen(next) {
    isOpen = next;
    root.classList.toggle('open', isOpen);
    launcher.setAttribute('aria-expanded', String(isOpen));
    launcher.setAttribute('aria-label', isOpen ? t('botClose') : t('botOpen'));
    if (isOpen) window.setTimeout(() => input.focus(), 180);
    keepInViewport();
  }

  function setHidden(next) {
    if (next) setOpen(false);
    root.classList.toggle('is-hidden', next);
    localStorage.setItem(VISIBILITY_KEY, next ? 'true' : 'false');
    window.requestAnimationFrame(keepInViewport);
  }

  function restorePosition() {
    try {
      const saved = JSON.parse(localStorage.getItem(POSITION_KEY));
      if (Number.isFinite(saved?.x) && Number.isFinite(saved?.y)) {
        root.style.left = `${saved.x}px`;
        root.style.top = `${saved.y}px`;
        root.style.right = 'auto';
        root.style.bottom = 'auto';
      }
    } catch (_) {
      localStorage.removeItem(POSITION_KEY);
    }
  }

  function keepInViewport() {
    const rect = root.getBoundingClientRect();
    if (root.style.left && root.style.top) {
      const x = clamp(rect.left, EDGE_GAP, window.innerWidth - rect.width - EDGE_GAP);
      const y = clamp(rect.top, EDGE_GAP, window.innerHeight - rect.height - EDGE_GAP);
      root.style.left = `${x}px`;
      root.style.top = `${y}px`;
    }
    updateFloatingPlacement();
  }

  function updateFloatingPlacement() {
    const rect = root.getBoundingClientRect();
    const panelWidth = Math.min(360, window.innerWidth - (EDGE_GAP * 2));
    const panelHeight = Math.min(460, Math.max(260, window.innerHeight - 150));
    const panelLeft = clamp(rect.left, EDGE_GAP, window.innerWidth - panelWidth - EDGE_GAP);
    const spaceAbove = rect.top - EDGE_GAP;
    const spaceBelow = window.innerHeight - rect.bottom - EDGE_GAP;
    let panelTop;

    if (spaceAbove >= panelHeight + EDGE_GAP) {
      panelTop = rect.top - panelHeight - EDGE_GAP;
    } else if (spaceBelow >= panelHeight + EDGE_GAP) {
      panelTop = rect.bottom + EDGE_GAP;
    } else {
      panelTop = clamp(rect.top - (panelHeight / 2), EDGE_GAP, window.innerHeight - panelHeight - EDGE_GAP);
    }

    root.style.setProperty('--bot-panel-x', `${Math.round(panelLeft - rect.left)}px`);
    root.style.setProperty('--bot-panel-y', `${Math.round(panelTop - rect.top)}px`);
    root.dataset.speechSide = rect.left < (window.innerWidth / 2) ? 'right' : 'left';
  }

  function enableDrag(handle) {
    let activePointer = null;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTop = 0;

    handle.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || event.target.closest('input, .bot-close')) return;
      const rect = root.getBoundingClientRect();
      activePointer = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startLeft = rect.left;
      startTop = rect.top;
      dragged = false;
      root.style.left = `${rect.left}px`;
      root.style.top = `${rect.top}px`;
      root.style.right = 'auto';
      root.style.bottom = 'auto';
      root.classList.add('dragging');
      handle.setPointerCapture(event.pointerId);
    });

    handle.addEventListener('pointermove', (event) => {
      if (event.pointerId !== activePointer) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (Math.abs(dx) + Math.abs(dy) > 6) dragged = true;
      const x = clamp(startLeft + dx, EDGE_GAP, window.innerWidth - root.offsetWidth - EDGE_GAP);
      const y = clamp(startTop + dy, EDGE_GAP, window.innerHeight - root.offsetHeight - EDGE_GAP);
      root.style.left = `${x}px`;
      root.style.top = `${y}px`;
      updateFloatingPlacement();
    });

    const finishDrag = (event) => {
      if (event.pointerId !== activePointer) return;
      activePointer = null;
      root.classList.remove('dragging');
      if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
      const rect = root.getBoundingClientRect();
      localStorage.setItem(POSITION_KEY, JSON.stringify({ x: Math.round(rect.left), y: Math.round(rect.top) }));
    };

    handle.addEventListener('pointerup', finishDrag);
    handle.addEventListener('pointercancel', finishDrag);
  }

  async function sendMessage() {
    const val = input.value.trim();
    if (!val) return;

    body.appendChild(h('div', { class: 'msg user' }, val));
    input.value = '';
    body.scrollTop = body.scrollHeight;

    const typing = h('div', { class: 'msg bot typing' }, '…');
    body.appendChild(typing);
    body.scrollTop = body.scrollHeight;

    try {
      const { reply } = await botService.send(val);
      body.removeChild(typing);
      body.appendChild(h('div', { class: 'msg bot' }, reply));
    } catch (err) {
      body.removeChild(typing);
      body.appendChild(h('div', { class: 'msg bot' }, t('errorTitle')));
    }
    body.scrollTop = body.scrollHeight;
  }

  send.addEventListener('click', sendMessage);
  input.addEventListener('keydown', (event) => { if (event.key === 'Enter') sendMessage(); });
  close.addEventListener('click', () => setOpen(false));
  hide.addEventListener('click', () => setHidden(true));
  showTab.addEventListener('click', () => setHidden(false));
  launcher.addEventListener('click', () => {
    if (dragged) {
      dragged = false;
      return;
    }
    setOpen(!isOpen);
  });

  enableDrag(launcher);
  enableDrag(dragHandle);
  window.addEventListener('resize', keepInViewport, { passive: true });
  restorePosition();
  if (localStorage.getItem(VISIBILITY_KEY) === 'true') root.classList.add('is-hidden');
  window.requestAnimationFrame(keepInViewport);
  return root;
}
